import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CoreService } from '../../../services/core.service';
import { ArticleImagesCarouselI, PostingQueueArticleI } from '../../../interfaces/core.interface';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-social-ai-articles-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './social-ai-articles-form.component.html',
  styleUrls: ['./social-ai-articles-form.component.scss']
})
export class SocialAiArticlesFormComponent implements OnInit {

  articleForm: FormGroup;
  previewUrl: string | null = null;
  previewIsImage: boolean = true;
  previewIsVideo: boolean = false;
  isPhotolinkPost: boolean = false;

  carouselImages: ArticleImagesCarouselI[] = [];
  isCarouselPost: boolean = false;

  hideSubmitButton: boolean = false;

  constructor(
    private coreService: CoreService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<SocialAiArticlesFormComponent>,

    @Inject(MAT_DIALOG_DATA) public data: { formData: PostingQueueArticleI, isFailedArticle?: boolean }
  ) {
    this.articleForm = this.fb.group({
      id: [''],
      rss_title: ['', Validators.required],
      rss_description: ['', Validators.required],
      switchboard_response_img: [''],
      createomate_video_url: [''],
      rss_article_url: [''],
      images: [''],
      error_response_message: [''],
      tag: [''],
    });
  }

  ngOnInit(): void {
  if (this.data && this.data.formData) {
    this.articleForm.patchValue(this.data.formData);

    this.hideSubmitButton = !!this.data.isFailedArticle;

    if (typeof this.data.formData.tag === 'string' && this.data.formData.tag === 'photolink') {
      this.isPhotolinkPost = true;
      this.previewUrl = null;
      this.previewIsImage = false;
      this.previewIsVideo = false;
      return; 
    }

    if (
      typeof this.data.formData.tag === 'string' &&
       ['igcarousel', 'tiktokcarousel'].includes(this.data.formData.tag) &&
      this.data.formData.images
    ) {
      try {
        this.carouselImages = JSON.parse(this.data.formData.images);
        this.isCarouselPost = true;
        this.previewUrl = null;
        this.previewIsImage = true;
        this.previewIsVideo = false;
      } catch (e) {
        console.error("Error parsing carousel images JSON:", e);
        this.carouselImages = [];
        this.isCarouselPost = false;
        this.setSingleMediaPreview(this.data.formData);
      }
    } else {
      this.isCarouselPost = false;
      this.setSingleMediaPreview(this.data.formData);
    }
  }
}

  private setSingleMediaPreview(articleData: PostingQueueArticleI): void {
    const videoUrl = articleData.createomate_video_url;
    const imageUrl = articleData.switchboard_response_img;
    const articleTag = articleData.tag;

    this.previewUrl = null;
    this.previewIsImage = false;
    this.previewIsVideo = false;

    const isVideoPost = (typeof articleTag === 'string' && (articleTag === 'fbvideo' || articleTag === 'igvideo' || articleTag === 'tiktokvideo'));

    if (isVideoPost) {
        if (videoUrl) {
            this.previewUrl = videoUrl;
            this.previewIsVideo = true;
        }
    } else {
        if (imageUrl) {
            this.previewUrl = imageUrl;
            this.previewIsImage = true;
        } else if (videoUrl) {
            this.previewUrl = videoUrl;
            this.previewIsVideo = true;
        }
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const originalTag = this.articleForm.get('tag')?.value; 

    this.coreService.uploadFile('uploadImageSocialAi', file).subscribe(res => {
      if (res.status === 'success') {
        const url = res.image_url;

        const mediaPatch: any = {
          switchboard_response_img: '',
          createomate_video_url: '',   
          images: ''                   
        };

        let newTag = originalTag;

        if (file.type.startsWith('image/')) {
          mediaPatch.switchboard_response_img = url;
          this.previewUrl = url;
          this.previewIsImage = true;
          this.previewIsVideo = false;

          if (originalTag === 'fbvideo') {
            newTag = 'fbphotopost';
          } else if (originalTag === 'igvideo') { 
            newTag = 'igphotopost';
          }

        } else if (file.type.startsWith('video/')) {
          mediaPatch.createomate_video_url = url;
          this.previewUrl = url;
          this.previewIsImage = false;
          this.previewIsVideo = true;

          if (originalTag === 'fbphotopost' || originalTag === 'fbfirstcomment') {
            newTag = 'fbvideo';
          } else if (originalTag === 'igphotopost') { 
            newTag = 'igvideo';
          }
        }

        this.articleForm.patchValue(mediaPatch);

        if (newTag !== originalTag) {
          this.articleForm.patchValue({ tag: newTag });
        }

        this.carouselImages = []; 
        this.isCarouselPost = false; 

      }
    }, error => {
      console.error('Error uploading file:', error);
      this.coreService.showSnackbar('File upload failed.');
    });
  }

  onSubmit() {
    if (this.hideSubmitButton) {
      return;
    }

    if (this.articleForm.valid) {
      const id = this.data?.formData?.posting_queue_id;

      const formValue = {
        rss_title: this.articleForm.value.rss_title,
        rss_description: this.articleForm.value.rss_description,
        switchboard_response_img: this.articleForm.value.switchboard_response_img,
        createomate_video_url: this.articleForm.value.createomate_video_url,
        rss_article_url: this.articleForm.value.rss_article_url,
        images: this.articleForm.value.images,
        tag: this.articleForm.value.tag
      };

      this.coreService.postData(`updateArticleBeforePublishing/${id}`, formValue).subscribe(data => {
    
        this.coreService.showSnackbar('Article updated successfully!');
        this.dialogRef.close(formValue);
      }, error => {
        console.error('Error updating article:', error);
        this.coreService.showSnackbar('Failed to update article.');
      });
    } else {
      this.articleForm.markAllAsTouched();
      this.coreService.showSnackbar('Please fill in all required fields.');
    }
  }
  closeDialog(): void {
    this.dialogRef.close();
  }
}